/**
 * Route table.
 *
 * The client never hand-writes a URL string. Every path is built from here, so
 * renaming an endpoint is a compile error across the whole repo instead of a
 * 404 discovered in production.
 */
export const API_VERSION = 'v1';
const base = `/${API_VERSION}`;
export const API_ROUTES = {
    health: {
        live: '/health/live',
        ready: '/health/ready',
        /** What the public status page shows. */
        status: '/health/status',
    },
    session: {
        current: `${base}/session`,
        profile: `${base}/session/profile`,
        /** Said once by the browser right after a successful sign-in, so the trail has it. */
        signedIn: `${base}/session/signed-in`,
    },
    organizations: {
        list: `${base}/organizations`,
        create: `${base}/organizations`,
        byId: (organizationId) => `${base}/organizations/${organizationId}`,
        /** The setup questions: province, trade, fiscal year, GST number, filing frequency. */
        setup: (organizationId) => `${base}/organizations/${organizationId}/setup`,
        /** The monthly ceiling on AI reading for one business. */
        aiCeiling: (organizationId) => `${base}/organizations/${organizationId}/ai-ceiling`,
    },
    catchUp: {
        /** How close the books are, month by month, and what is left. */
        report: (organizationId) => `${base}/organizations/${organizationId}/catch-up`,
        /** Finish the reading, file the certain, match the bank, rebuild returns. */
        run: (organizationId) => `${base}/organizations/${organizationId}/catch-up/run`,
        /** Sign every record the system is sure of. A person's click. */
        sign: (organizationId) => `${base}/organizations/${organizationId}/catch-up/sign`,
        /** Ask the client about payments with no paper; one message to send. */
        ask: (organizationId) => `${base}/organizations/${organizationId}/catch-up/ask`,
    },
    account: {
        /** Create an account and its login. Public. */
        signUp: `${base}/sign-up`,
        /** Email a link to choose a new password. Public; says the same thing either way. */
        passwordReset: `${base}/password-reset`,
        /** Say whether this login is a firm or a business. Idempotent. */
        start: `${base}/account/start`,
    },
    billing: {
        overview: `${base}/billing`,
        /** A payment page for the caller's account. */
        checkout: `${base}/billing/checkout`,
        /** The provider's page for cards, invoices and cancelling. */
        portal: `${base}/billing/portal`,
        /** The payment provider's events. Signed, not authenticated. */
        webhook: `${base}/billing/webhook`,
    },
    memberships: {
        list: (organizationId) => `${base}/organizations/${organizationId}/memberships`,
        byId: (organizationId, membershipId) => `${base}/organizations/${organizationId}/memberships/${membershipId}`,
    },
    vehicles: {
        /** Each vehicle with the year's costs and its business share; ?fiscalYear=. POST saves one. */
        list: (organizationId) => `${base}/organizations/${organizationId}/vehicles`,
        archive: (organizationId, vehicleId) => `${base}/organizations/${organizationId}/vehicles/${vehicleId}/archive`,
        /** Trips; ?fiscalYear=. POST adds one. */
        mileage: (organizationId) => `${base}/organizations/${organizationId}/vehicles/mileage`,
        mileageEntry: (organizationId, logId) => `${base}/organizations/${organizationId}/vehicles/mileage/${logId}`,
        /** The year-end adjustment for personal use. */
        postAdjustment: (organizationId) => `${base}/organizations/${organizationId}/vehicles/adjustment`,
    },
    yearEnd: {
        /** The year's state and checklist; ?fiscalYear=2026. */
        status: (organizationId) => `${base}/organizations/${organizationId}/year-end/status`,
        /** The month the fiscal year ends. */
        fiscalYearEnd: (organizationId) => `${base}/organizations/${organizationId}/year-end/fiscal-year-end`,
        /** Payments to subcontractors for the slips; ?fiscalYear=. */
        t5018: (organizationId) => `${base}/organizations/${organizationId}/year-end/t5018`,
        /** Schedule 8 by class; GET ?fiscalYear=, PUT one class's inputs. */
        cca: (organizationId) => `${base}/organizations/${organizationId}/year-end/cca`,
        /** Posts the year's CCA as one adjusting entry. */
        postCca: (organizationId) => `${base}/organizations/${organizationId}/year-end/cca/post`,
        close: (organizationId) => `${base}/organizations/${organizationId}/year-end/close`,
        reopen: (organizationId) => `${base}/organizations/${organizationId}/year-end/reopen`,
    },
    payroll: {
        /** Pay runs; POST writes a new draft. */
        list: (organizationId) => `${base}/organizations/${organizationId}/payroll/runs`,
        byId: (organizationId, runId) => `${base}/organizations/${organizationId}/payroll/runs/${runId}`,
        post: (organizationId, runId) => `${base}/organizations/${organizationId}/payroll/runs/${runId}/post`,
        void: (organizationId, runId) => `${base}/organizations/${organizationId}/payroll/runs/${runId}/void`,
        /** What payroll still owes: the CRA, the employees, anyone else. */
        liabilities: (organizationId) => `${base}/organizations/${organizationId}/payroll/liabilities`,
    },
    questions: {
        /** Questions a business has sent its client; POST asks one and returns the link once. */
        list: (organizationId) => `${base}/organizations/${organizationId}/questions`,
        withdraw: (organizationId, questionId) => `${base}/organizations/${organizationId}/questions/${questionId}/withdraw`,
        /** Public, the token is the credential: what is being asked. */
        preview: (token) => `${base}/q/${token}`,
        answer: (token) => `${base}/q/${token}/answer`,
        uploadTicket: (token) => `${base}/q/${token}/upload-ticket`,
        file: (token) => `${base}/q/${token}/file`,
    },
    invitations: {
        /** Pending invitations into a business; POST creates one and returns its link once. */
        list: (organizationId) => `${base}/organizations/${organizationId}/invitations`,
        byId: (organizationId, invitationId) => `${base}/organizations/${organizationId}/invitations/${invitationId}`,
        /** What a link is for. Public: the token is the credential. */
        preview: (token) => `${base}/invitations/${token}`,
        /** Create the account the invitation is addressed to. Public. */
        register: (token) => `${base}/invitations/${token}/register`,
        /** Take up the invitation as the signed-in person. */
        accept: (token) => `${base}/invitations/${token}/accept`,
    },
    audit: {
        list: (organizationId) => `${base}/organizations/${organizationId}/audit-events`,
    },
    storage: {
        uploadUrl: (organizationId) => `${base}/organizations/${organizationId}/storage/uploads`,
        downloadUrl: (organizationId) => `${base}/organizations/${organizationId}/storage/downloads`,
    },
    intake: {
        createSession: (organizationId) => `${base}/organizations/${organizationId}/intake/sessions`,
        completeSession: (organizationId, sessionId) => `${base}/organizations/${organizationId}/intake/sessions/${sessionId}/complete`,
        abandonSession: (organizationId, sessionId) => `${base}/organizations/${organizationId}/intake/sessions/${sessionId}/abandon`,
        stats: (organizationId) => `${base}/organizations/${organizationId}/intake/stats`,
        /** The Inbox triage line: what arrived, and what of it needs a person. */
        inboxSummary: (organizationId) => `${base}/organizations/${organizationId}/intake/inbox`,
    },
    documents: {
        list: (organizationId) => `${base}/organizations/${organizationId}/documents`,
        byId: (organizationId, documentId) => `${base}/organizations/${organizationId}/documents/${documentId}`,
        download: (organizationId, documentId) => `${base}/organizations/${organizationId}/documents/${documentId}/download`,
        retry: (organizationId, documentId) => `${base}/organizations/${organizationId}/documents/${documentId}/retry`,
        archive: (organizationId, documentId) => `${base}/organizations/${organizationId}/documents/${documentId}/archive`,
    },
    extraction: {
        /** Latest run for a document, with its fields and validation findings. */
        latest: (organizationId, documentId) => `${base}/organizations/${organizationId}/documents/${documentId}/extraction`,
        /** Every attempt, newest first — the audit trail for a prompt change. */
        history: (organizationId, documentId) => `${base}/organizations/${organizationId}/documents/${documentId}/extractions`,
        byId: (organizationId, documentId, extractionId) => `${base}/organizations/${organizationId}/documents/${documentId}/extractions/${extractionId}`,
        /** Queue a document for extraction, or re-extract it. */
        request: (organizationId, documentId) => `${base}/organizations/${organizationId}/documents/${documentId}/extract`,
        stats: (organizationId) => `${base}/organizations/${organizationId}/extraction/stats`,
    },
    bookkeeping: {
        list: (organizationId) => `${base}/organizations/${organizationId}/bookkeeping`,
        stats: (organizationId) => `${base}/organizations/${organizationId}/bookkeeping/stats`,
        settings: (organizationId) => `${base}/organizations/${organizationId}/bookkeeping/settings`,
        /** The pick lists a record editor needs, in one round trip. */
        reference: (organizationId) => `${base}/organizations/${organizationId}/bookkeeping/reference`,
        byId: (organizationId, recordId) => `${base}/organizations/${organizationId}/bookkeeping/${recordId}`,
        /** Confirming is what a bookkeeper does; it still exports nothing. */
        confirm: (organizationId, recordId) => `${base}/organizations/${organizationId}/bookkeeping/${recordId}/confirm`,
        /** A draft whose reading was wrong goes back to Review; returns the review item. */
        sendBack: (organizationId, recordId) => `${base}/organizations/${organizationId}/bookkeeping/${recordId}/send-back`,
        /** A record that should never have existed. Postings reversed, nothing deleted. */
        void: (organizationId, recordId) => `${base}/organizations/${organizationId}/bookkeeping/${recordId}/void`,
        /** A signed record superseded by a draft copy to fix and sign; returns the copy. */
        correct: (organizationId, recordId) => `${base}/organizations/${organizationId}/bookkeeping/${recordId}/correct`,
        /** The last signed record from the same supplier, to code this one the same way. */
        lastTime: (organizationId, recordId) => `${base}/organizations/${organizationId}/bookkeeping/${recordId}/last-time`,
        /** One decision applied to many drafts. */
        applyMany: (organizationId) => `${base}/organizations/${organizationId}/bookkeeping/apply`,
        /** Sign a selection of drafts; each is re-checked and the skipped ones say why. */
        confirmMany: (organizationId) => `${base}/organizations/${organizationId}/bookkeeping/confirm-many`,
    },
    intelligence: {
        /** Everything the engine has observed about one supplier. */
        vendor: (organizationId, vendorId) => `${base}/organizations/${organizationId}/intelligence/vendors/${vendorId}`,
        /** Suggestions, anomalies, duplicates and job candidates for one record. */
        record: (organizationId, recordId) => `${base}/organizations/${organizationId}/intelligence/records/${recordId}`,
        settings: (organizationId) => `${base}/organizations/${organizationId}/intelligence/settings`,
        /** Stated confidence against what actually happened. */
        calibration: (organizationId) => `${base}/organizations/${organizationId}/intelligence/calibration`,
        /** Recompute every statistic from the ledger. */
        rebuild: (organizationId) => `${base}/organizations/${organizationId}/intelligence/rebuild`,
    },
    customers: {
        list: (organizationId) => `${base}/organizations/${organizationId}/customers`,
        detail: (organizationId, customerId) => `${base}/organizations/${organizationId}/customers/${customerId}`,
    },
    invoices: {
        /** The list; POST writes a new draft. */
        list: (organizationId) => `${base}/organizations/${organizationId}/invoices`,
        /** One invoice as a document; PATCH rewrites a draft. */
        byId: (organizationId, invoiceId) => `${base}/organizations/${organizationId}/invoices/${invoiceId}`,
        /** Numbers the draft and writes its signed revenue record. */
        issue: (organizationId, invoiceId) => `${base}/organizations/${organizationId}/invoices/${invoiceId}/issue`,
        /** Voids an issued invoice with a reason; its record is reversed. */
        void: (organizationId, invoiceId) => `${base}/organizations/${organizationId}/invoices/${invoiceId}/void`,
        /** Numbering and the business details printed at the top. */
        settings: (organizationId) => `${base}/organizations/${organizationId}/invoices/settings`,
    },
    jobs: {
        list: (organizationId) => `${base}/organizations/${organizationId}/jobs`,
        detail: (organizationId, jobId) => `${base}/organizations/${organizationId}/jobs/${jobId}`,
    },
    objects: {
        relationships: (organizationId, entityType, entityId) => `${base}/organizations/${organizationId}/objects/${entityType}/${entityId}/relationships`,
        summary: (organizationId, entityType, entityId) => `${base}/organizations/${organizationId}/objects/${entityType}/${entityId}/summary`,
    },
    equipment: {
        list: (organizationId) => `${base}/organizations/${organizationId}/equipment`,
        detail: (organizationId, equipmentId) => `${base}/organizations/${organizationId}/equipment/${equipmentId}`,
    },
    /** Everything a business owns, downloadable. */
    dataExports: {
        list: (organizationId) => `${base}/organizations/${organizationId}/data-exports`,
        part: (organizationId, exportId, index) => `${base}/organizations/${organizationId}/data-exports/${exportId}/parts/${index}`,
    },
    /** A business asking to leave: thirty days, the owner's alone. */
    deletion: {
        request: (organizationId) => `${base}/organizations/${organizationId}/deletion`,
    },
    integrations: {
        /** The catalog of systems a business can connect, with each one's state. */
        catalog: (organizationId) => `${base}/organizations/${organizationId}/integrations`,
        connection: (organizationId, provider) => `${base}/organizations/${organizationId}/integrations/${provider}/connection`,
        sync: (organizationId, provider) => `${base}/organizations/${organizationId}/integrations/${provider}/sync`,
        /** Start a sign-in with the other company; answers with where to send the person. */
        oauthStart: (organizationId, provider) => `${base}/organizations/${organizationId}/integrations/${provider}/oauth`,
        /** Where the other company sends the person back. Public: the state proves who. */
        oauthCallback: (provider) => `${base}/integrations/oauth/${provider}/callback`,
    },
    /** A spreadsheet exported from any CRM, mapped once and remembered. */
    imports: {
        history: (organizationId) => `${base}/organizations/${organizationId}/imports`,
        preview: (organizationId) => `${base}/organizations/${organizationId}/imports/preview`,
        suggest: (organizationId) => `${base}/organizations/${organizationId}/imports/suggest`,
        run: (organizationId) => `${base}/organizations/${organizationId}/imports/run`,
    },
    /** The accountant's calendar: the month you close, the return you file. */
    close: {
        periods: (organizationId) => `${base}/organizations/${organizationId}/close/periods`,
        period: (organizationId, periodId) => `${base}/organizations/${organizationId}/close/periods/${periodId}`,
        readiness: (organizationId, periodId) => `${base}/organizations/${organizationId}/close/periods/${periodId}/readiness`,
        signOff: (organizationId, periodId) => `${base}/organizations/${organizationId}/close/periods/${periodId}/sign-off`,
        returns: (organizationId) => `${base}/organizations/${organizationId}/close/returns`,
        returnBuild: (organizationId, returnId) => `${base}/organizations/${organizationId}/close/returns/${returnId}/build`,
        returnDetail: (organizationId, returnId, line) => `${base}/organizations/${organizationId}/close/returns/${returnId}/detail/${line}`,
        returnFile: (organizationId, returnId) => `${base}/organizations/${organizationId}/close/returns/${returnId}/file`,
    },
    reports: {
        monthlyFlows: (organizationId) => `${base}/organizations/${organizationId}/reports/monthly-flows`,
        topCategories: (organizationId) => `${base}/organizations/${organizationId}/reports/top-categories`,
        topSuppliers: (organizationId) => `${base}/organizations/${organizationId}/reports/top-suppliers`,
        profitAndLoss: (organizationId) => `${base}/organizations/${organizationId}/reports/profit-and-loss`,
        trialBalance: (organizationId) => `${base}/organizations/${organizationId}/reports/trial-balance`,
        balanceSheet: (organizationId) => `${base}/organizations/${organizationId}/reports/balance-sheet`,
        bankReconciliation: (organizationId) => `${base}/organizations/${organizationId}/reports/bank-reconciliation`,
        /** Who owes what, and how late. */
        receivables: (organizationId) => `${base}/organizations/${organizationId}/reports/receivables`,
        /** Who we owe, when it is due, and how late. */
        payables: (organizationId) => `${base}/organizations/${organizationId}/reports/payables`,
        /** One client's month in one row, for the firm's desk. */
        clientSummary: (organizationId) => `${base}/organizations/${organizationId}/reports/client-summary`,
        gifi: (organizationId) => `${base}/organizations/${organizationId}/reports/gifi`,
    },
    query: {
        run: (organizationId) => `${base}/organizations/${organizationId}/query`,
        collections: (organizationId) => `${base}/organizations/${organizationId}/collections`,
        collection: (organizationId, collectionId) => `${base}/organizations/${organizationId}/collections/${collectionId}`,
    },
    search: {
        /** Everything, the way people remember it: words, half-words, amounts. */
        query: (organizationId) => `${base}/organizations/${organizationId}/search`,
    },
    bank: {
        accounts: (organizationId) => `${base}/organizations/${organizationId}/bank/accounts`,
        /** Statements the router recognised that nobody has imported yet. */
        statements: (organizationId) => `${base}/organizations/${organizationId}/bank/statements`,
        statementPreview: (organizationId, documentId) => `${base}/organizations/${organizationId}/bank/statements/${documentId}`,
        statementImport: (organizationId, documentId) => `${base}/organizations/${organizationId}/bank/statements/${documentId}/import`,
        /** Read a PDF statement again, after a reading that did not prove. */
        statementReread: (organizationId, documentId) => `${base}/organizations/${organizationId}/bank/statements/${documentId}/reread`,
        transactions: (organizationId) => `${base}/organizations/${organizationId}/bank/transactions`,
        confirm: (organizationId, bankTransactionId) => `${base}/organizations/${organizationId}/bank/transactions/${bankTransactionId}/confirm`,
        dismiss: (organizationId, bankTransactionId) => `${base}/organizations/${organizationId}/bank/transactions/${bankTransactionId}/dismiss`,
        explain: (organizationId, bankTransactionId) => `${base}/organizations/${organizationId}/bank/transactions/${bankTransactionId}/explain`,
        /** Every waiting line, looked at again against every record. */
        /** Money out, explained: net pay, a remittance, a transfer, a loan payment, a draw. */
        explainPayment: (organizationId, bankTransactionId) => `${base}/organizations/${organizationId}/bank/transactions/${bankTransactionId}/explain-payment`,
        rematch: (organizationId) => `${base}/organizations/${organizationId}/bank/rematch`,
    },
    ledger: {
        /** The chart of accounts with each account's GIFI code. */
        chart: (organizationId) => `${base}/organizations/${organizationId}/ledger/chart`,
        accountGifi: (organizationId, accountId) => `${base}/organizations/${organizationId}/ledger/chart/${accountId}/gifi`,
        openingBalances: (organizationId) => `${base}/organizations/${organizationId}/ledger/opening-balances`,
        /** Last year's GIFI items, placed in this business's accounts. Changes nothing. */
        openingFromGifi: (organizationId) => `${base}/organizations/${organizationId}/ledger/opening-balances/from-gifi`,
        /** The accountant's adjusting entries: list them, or post one. */
        adjustments: (organizationId) => `${base}/organizations/${organizationId}/ledger/adjustments`,
        /** Undo an adjustment with an equal and opposite entry. */
        reverseAdjustment: (organizationId, entryId) => `${base}/organizations/${organizationId}/ledger/adjustments/${entryId}/reverse`,
    },
    vendors: {
        list: (organizationId) => `${base}/organizations/${organizationId}/vendors`,
        create: (organizationId) => `${base}/organizations/${organizationId}/vendors`,
        byId: (organizationId, vendorId) => `${base}/organizations/${organizationId}/vendors/${vendorId}`,
        aliases: (organizationId, vendorId) => `${base}/organizations/${organizationId}/vendors/${vendorId}/aliases`,
        /** A spelling taught by mistake sends every future receipt to the wrong
         *  supplier, so it must be removable. */
        alias: (organizationId, vendorId, aliasId) => `${base}/organizations/${organizationId}/vendors/${vendorId}/aliases/${aliasId}`,
        taxInformation: (organizationId, vendorId) => `${base}/organizations/${organizationId}/vendors/${vendorId}/tax-information`,
        verifyTaxInformation: (organizationId, vendorId, taxInfoId) => `${base}/organizations/${organizationId}/vendors/${vendorId}/tax-information/${taxInfoId}/verify`,
        /** Two suppliers that turn out to be one. */
        merge: (organizationId, vendorId) => `${base}/organizations/${organizationId}/vendors/${vendorId}/merge`,
    },
    pulse: {
        /** The five things worth an owner's morning. */
        current: (organizationId) => `${base}/organizations/${organizationId}/pulse`,
        dismiss: (organizationId) => `${base}/organizations/${organizationId}/pulse/dismiss`,
        /** Ask a question. The model picks from a menu; the ledger computes. */
        ask: (organizationId) => `${base}/organizations/${organizationId}/pulse/ask`,
    },
    exports: {
        list: (organizationId) => `${base}/organizations/${organizationId}/exports`,
        /** What is still open in a period, before anybody builds a file. */
        /** The receipt register, in the nine columns a bookkeeper types by hand. */
        register: (organizationId) => `${base}/organizations/${organizationId}/exports/register`,
        readiness: (organizationId) => `${base}/organizations/${organizationId}/exports/readiness`,
        create: (organizationId) => `${base}/organizations/${organizationId}/exports`,
        /** The file itself. Reproducible from the ledger, byte for byte. */
        download: (organizationId, exportId) => `${base}/organizations/${organizationId}/exports/${exportId}/file`,
    },
    tax: {
        /** What can be claimed, what is at risk, and what has not been checked. */
        insights: (organizationId) => `${base}/organizations/${organizationId}/tax/insights`,
        /** Every decision made about one bookkeeping record. */
        decisions: (organizationId, recordId) => `${base}/organizations/${organizationId}/tax/records/${recordId}/decisions`,
        /** Ask the model to put one decision into plain English. */
        explain: (organizationId, decisionId) => `${base}/organizations/${organizationId}/tax/decisions/${decisionId}/explain`,
        /** The rule sets in force, and whether anybody has checked them. */
        packs: (organizationId) => `${base}/organizations/${organizationId}/tax/packs`,
        /** Reconsider every draft record after a rule or rate changes. */
        redecide: (organizationId) => `${base}/organizations/${organizationId}/tax/redecide`,
        /** Record that a person has checked a rule pack against the legislation. */
        verifyPack: (organizationId, packId) => `${base}/organizations/${organizationId}/tax/packs/${packId}/verify`,
    },
    review: {
        /** File everything that needs nobody, as a draft. */
        /** Grade every waiting document again, against the validator as it is now. */
        regrade: (organizationId) => `${base}/organizations/${organizationId}/review/regrade`,
        fileCertain: (organizationId) => `${base}/organizations/${organizationId}/review/file-certain`,
        /** Waiting documents grouped by a supplier nobody has decided about yet. */
        suppliersToTeach: (organizationId) => `${base}/organizations/${organizationId}/review/suppliers`,
        setAsideDuplicates: (organizationId) => `${base}/organizations/${organizationId}/review/duplicates`,
        /** The queue. Organisation is a parameter, not an assumption — a
         *  cross-client queue for an accounting firm is a new route over the same
         *  query, not a rewrite. */
        list: (organizationId) => `${base}/organizations/${organizationId}/review`,
        stats: (organizationId) => `${base}/organizations/${organizationId}/review/stats`,
        byId: (organizationId, itemId) => `${base}/organizations/${organizationId}/review/${itemId}`,
        /** Opening an item starts its clock — the other half of seconds_to_decide. */
        open: (organizationId, itemId) => `${base}/organizations/${organizationId}/review/${itemId}/open`,
        draft: (organizationId, itemId) => `${base}/organizations/${organizationId}/review/${itemId}/draft`,
        submit: (organizationId, itemId) => `${base}/organizations/${organizationId}/review/${itemId}/submit`,
        notes: (organizationId, itemId) => `${base}/organizations/${organizationId}/review/${itemId}/notes`,
    },
    queue: {
        list: (organizationId) => `${base}/organizations/${organizationId}/queue/jobs`,
        retry: (organizationId, jobId) => `${base}/organizations/${organizationId}/queue/jobs/${jobId}/retry`,
    },
};
/** Header carrying the correlation id on both requests and responses. */
export const REQUEST_ID_HEADER = 'x-request-id';
//# sourceMappingURL=routes.js.map